/**
 * Device severity status constants
 * These represent the possible severity levels returned from device status APIs
 */
export const SEVERITY_STATUSES = {
	/** Healthy/Normal statuses - device operating normally */
	HEALTHY: ['ok', 'normal', 'good', 'info'] as const,

	/** Warning statuses - minor issues detected */
	WARNING: ['warning', 'minor'] as const,

	/** Critical statuses - major issues requiring attention */
	CRITICAL: ['critical', 'major', 'error', 'disconnected'] as const
} as const

/**
 * All possible severity status values
 */
export type SeverityStatus =
	| (typeof SEVERITY_STATUSES.HEALTHY)[number]
	| (typeof SEVERITY_STATUSES.WARNING)[number]
	| (typeof SEVERITY_STATUSES.CRITICAL)[number]

/**
 * Checks if a severity indicates a healthy device state
 * @param severity - The severity level to check
 * @returns True if severity is in the healthy category
 */
export const isHealthySeverity = (severity?: string): boolean => {
	if (!severity) return false
	const normalized = severity.toLowerCase()
	return (SEVERITY_STATUSES.HEALTHY as readonly string[]).includes(normalized)
}

/**
 * Checks if a severity indicates a warning state
 * @param severity - The severity level to check
 * @returns True if severity is in the warning category
 */
export const isWarningSeverity = (severity?: string): boolean => {
	if (!severity) return false
	const normalized = severity.toLowerCase()
	return (SEVERITY_STATUSES.WARNING as readonly string[]).includes(normalized)
}

/**
 * Checks if a severity indicates a critical state
 * @param severity - The severity level to check
 * @returns True if severity is in the critical category
 */
export const isCriticalSeverity = (severity?: string): boolean => {
	if (!severity) return false
	const normalized = severity.toLowerCase()
	return (SEVERITY_STATUSES.CRITICAL as readonly string[]).includes(normalized)
}
